import { type MathfieldElement } from "mathlive";
import React, {
  useRef,
  useState,
  RefAttributes,
  HTMLAttributes,
  useCallback,
} from "react";
import ReactDOM from "react-dom";
import { usePopper } from "react-popper";
import styled from "styled-components";

import { Stem } from "@examspark/types";

import EquationEntryKeypad, { KeypadProps } from "./EquationEntryKeypad";
import MathfieldComponent from "./MathfieldComponent";

const StyledKeypad = styled(EquationEntryKeypad)``;

const StyledGapEntryWrapper = styled.div`
  & math-field::part(virtual-keyboard-toggle) {
    display: none;
  }
  & math-field::part(menu-toggle) {
    display: none;
  }

  math-field {
    background-color: #fff;
    vertical-align: middle;
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.3);
    padding: 0.5rem;
    min-width: 3rem;
    font-size: 1.5em;
    cursor: text;

    flex-grow: 1;
  }
`;

const StyledEditorEntryField = styled.div`
  & math-field::part(virtual-keyboard-toggle) {
    display: none;
  }
  & math-field::part(menu-toggle) {
    display: none;
  }

  math-field {
    background-color: #fff;
    width: 100%;
    box-sizing: border-box;
    border-radius: 0.2em;
    border: solid 1px #ccc;
    padding: 0.5em;
    transition: border-color 0.2s;
    font-family: inherit;
    font-size: 1.5em;
    cursor: text;
  }
`;

const EquationEntry = ({
  interactionType,
  value,
  onChange,
  mathKeyboardPreset,
}: {
  interactionType: "gap" | "graphic-gap" | "editor";
  mathKeyboardPreset?: Stem.MathKeyboardPreset;
  value?: string;
  onChange?: (value: any) => void;
}) => {
  const mathRef = useRef<MathfieldElement | null>(null);
  const [showKeypad, setShowKeypad] = useState(false);
  const [fieldElement, setFieldElement] = useState<any>(null);
  const [menuElement, setMenuElement] = useState<any>(null);
  const { styles, attributes, update } = usePopper(fieldElement, menuElement, {
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 5],
        },
      },
    ],
    placement: "bottom-start",
  });

  let StyledWrapper = StyledGapEntryWrapper;
  if (interactionType === "editor") {
    StyledWrapper = StyledEditorEntryField;
  }

  const initMathfield = useCallback((el: MathfieldElement) => {
    mathRef.current = el;

    if (el == null) {
      // unmount
      return;
    }

    // allegedly, they expect you to do all the initialization in the ref call
    el.defaultMode = "math";
    el.mathVirtualKeyboardPolicy = "manual";
    el.menuItems = [];

    el.inlineShortcuts = {
      ...el.inlineShortcuts,
      "*": "\\times",
    };
  }, []);

  return (
    <div
      ref={setFieldElement}
      style={
        interactionType === "gap"
          ? {
              display: "inline-block",
              verticalAlign: "middle",
              position: "relative",
            }
          : interactionType === "graphic-gap"
            ? {
                display: "flex",
                minWidth: "100%",
                minHeight: "100%",
              }
            : { position: "relative" }
      }
    >
      <StyledWrapper>
        <MathfieldComponent
          mathfieldRef={initMathfield}
          latex={value ?? ""}
          onInput={(el) => {
            const value = (el.target as MathfieldElement).value;
            onChange?.(value);
          }}
          onFocus={() => {
            setShowKeypad(true);
          }}
          onBlur={(e) => {
            setShowKeypad(false);
          }}
        />
      </StyledWrapper>
      {showKeypad && (
        <KeypadMenu
          ref={setMenuElement}
          // zIndex needs to be higher than ReactModalPortal's
          // defined in ~/packages/question-player/src/ItemPlayer.css
          style={{ ...styles.popper, zIndex: 10000 }}
          {...attributes.popper}
          layout={mathKeyboardPreset}
          onExecuteCommand={(cmd) => {
            mathRef.current?.executeCommand(cmd);
            setTimeout(() => {
              update?.();
            }, 250);
          }}
          onInsert={(value) => {
            mathRef.current?.insert(value);
            setTimeout(() => {
              update?.();
            }, 250);
          }}
        />
      )}
    </div>
  );
};

type KeypadMenuProps = RefAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement> &
  KeypadProps;

export const KeypadMenu: React.FC<React.PropsWithChildren<KeypadMenuProps>> =
  React.forwardRef<HTMLDivElement, KeypadMenuProps>(
    ({ onExecuteCommand, onInsert, layout, ...props }, ref) => {
      return ReactDOM.createPortal(
        <div ref={ref} {...props}>
          <StyledKeypad
            onExecuteCommand={onExecuteCommand}
            onInsert={onInsert}
            layout={layout}
          />
        </div>,
        document.getElementById("root")!,
      );
    },
  );

export default EquationEntry;
